import { Navigate, useLocation, useParams } from 'react-router-dom';

import { useShop } from '@/hooks/useShop';
import { pickDefaultShopId } from '@/lib/shop-status';
import { Loader } from '@/components/ui';

import { AppLayout } from './AppLayout';

const FullScreenLoader = () => (
	<div className="flex h-screen items-center justify-center bg-background">
		<Loader />
	</div>
);

/**
 * Decides the routing model from the shop count (see CLAUDE.md):
 *
 *   - 0 / 1 shop  → render the flat shell exactly as before (single-shop is
 *                   unchanged; AppLayout's own effects handle no-shops /
 *                   onboarding).
 *   - >1 shops    → redirect the flat path to its shop-scoped equivalent
 *                   `/{defaultShopId}{subPath}`, which the `/:shopId` route
 *                   then serves. Never renders AppLayout itself, so a
 *                   multi-shop user never flashes `shops[0]`'s dashboard.
 */
export function ShopRoutingGate() {
	const { shops, isLoading } = useShop();
	const location = useLocation();

	if (isLoading) return <FullScreenLoader />;
	if (shops.length <= 1) return <AppLayout />;

	const defaultShopId = pickDefaultShopId(shops);
	const subPath = location.pathname === '/' ? '' : location.pathname;
	return <Navigate to={`/${defaultShopId}${subPath}${location.search}`} replace />;
}

/**
 * Element for the `/:shopId/*` routes. Validates the id against the user's
 * shops and self-heals the URL:
 *
 *   - 0 shops               → flat `/` (gate renders the no-shops state)
 *   - 1 shop                → flat `/` (single-shop is never shop-scoped)
 *   - id not in the org     → redirect to the default shop
 *   - otherwise             → render the shell for that shop
 */
export function ShopScopedLayout() {
	const { shopId } = useParams();
	const { shops, isLoading } = useShop();

	if (isLoading) return <FullScreenLoader />;
	if (shops.length <= 1) return <Navigate to="/" replace />;
	if (!shops.some((s) => s.id === shopId)) return <Navigate to={`/${pickDefaultShopId(shops)}`} replace />;
	return <AppLayout />;
}
